import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  HostBinding,
  Injector,
  Input,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef } from '@angular/core';
import { debounceTime, firstValueFrom, map, switchMap } from 'rxjs';
import { BaseRendererV2 } from '../base-renderer-v2/base-renderer-v2.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * The best suits for rendering items whose sizes are always equal and does not change over time (not expandable or collapsable).
 * Prefer to use it for such cases as it feels smooth for user and better for UX.
 * Use gapBetweenItems input and avoid gap CSS property from being used as it affects item size calculation and this could
 * make scrolling not smooth/twitchy.
 * Use it like the following:
 * <app-cdk-virtual-scroll-renderer
 *  [items]="someItems"
 *  [idSelector]="selectId"
 *  [gapBetweenItems]="4"
 * >
 *  <ng-template #itemTemplate let-item="item">
 *    <div>{{item}}</div>
 *  </ng-template>
 * </app-cdk-virtual-scroll-renderer>
 */
@Component({
  selector: 'app-cdk-virtual-scroll-renderer',
  templateUrl: './cdk-virtual-scroll-renderer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CdkVirtualScrollRendererComponent extends BaseRendererV2 implements OnInit, AfterViewInit {
  @HostBinding('class')
  private readonly classes = 'block h-full';

  private viewPort: CdkVirtualScrollViewport;

  @ViewChildren('viewPort')
  private viewPortQueryList: QueryList<CdkVirtualScrollViewport>;

  @ViewChild('calculator', { read: ViewContainerRef, static: true })
  private calculatorContainerRef: ViewContainerRef;

  @ViewChild('itemWrapperTemplate', { static: true })
  protected itemWrapperTemplate: TemplateRef<any>;

  @ContentChild('itemTemplate', { static: true })
  protected itemTemplate: TemplateRef<any>;

  protected get gapClass(): string {
    return this.gapBetweenItems ? `pb-${this.gapBetweenItems}` : undefined;
  }

  itemSize: number;

  /** Number corresponding to tailwind sizes grid */
  @Input() gapBetweenItems: 1 | 2 | 3 | 4 | 5 | 6 | 7;

  constructor(injector: Injector, private changeDetector: ChangeDetectorRef) {
    super(injector);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngOnChanges(): void {
    super.ngOnChanges();
    this.calculateItemSize();
  }

  ngAfterViewInit(): void {
    const viewPort$ = this.viewPortQueryList.changes.pipe(map(() => this.viewPortQueryList.first));
    viewPort$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((viewPort) => {
      this.viewPort = viewPort;
      this.elementRef.nativeElement
        .querySelector('.cdk-virtual-scroll-content-wrapper')
        .classList.add('items-container', 'w-full');
    });
    viewPort$
      .pipe(
        switchMap((viewPort) => viewPort.elementScrolled()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.scrolled.emit());
    this.viewPortQueryList.notifyOnChanges();
  }

  protected itemTrackBy(index: number, item: any): any {
    return this.selectId(item) || index.toString();
  }

  protected getGapClass(): string {
    return this.gapBetweenItems ? `pb-${this.gapBetweenItems}` : undefined;
  }

  protected scrollToIdBaseAsync(itemId: string): Promise<void> {
    const index = this.itemsMap.get(itemId)?.index;

    if (index) {
      return new Promise((resolve, reject) => {
        firstValueFrom(this.viewPort.elementScrolled().pipe(debounceTime(500)))
          .then(() => resolve())
          .catch((error) => reject(error));
        this.viewPort.scrollToIndex(index, 'smooth');
      });
    }
  }

  // With this trick we won't need to manually specify item size
  // this function will append first item into dom
  // then will take height computed style, remove this item from dom
  // and enables virtual scroller with calculated item size
  private calculateItemSize(): void {
    if (!this.itemSize && this.items?.length) {
      this.changeDetector.detectChanges();
      this.calculatorContainerRef.createEmbeddedView(this.itemWrapperTemplate, { itemId: this.selectId(this.items[0]) }).detectChanges();
      const itemWrapperElement = this.elementRef.nativeElement.querySelector('.calculator .item-wrapper');
      const computedStyles = window.getComputedStyle(itemWrapperElement);
      const height = Number.parseInt(computedStyles.height.replace('px', ''));
      this.itemSize = height;
      this.changeDetector.detectChanges();
      this.calculatorContainerRef.clear();
      this.elementRef.nativeElement.querySelector('.calculator').remove();
    }
  }
}
